import { useEffect, useState } from "react"
import * as Sentry from "@sentry/react"
import Box from "@mui/material/Box"
import Link from "@mui/material/Link"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { ownerChanges } from "./lib"
import type { HeliusTx } from "./lib"
import { RPC_HOST } from "./useOwnedApes"

const LIMIT = 50
// Wallets of the team (update authority + creators), shown by name
const TEAM = new Set([
	"GYxL3r4CRwfJXUcQSCKaiX53JFRmPHg7tpjkqadpcmqf",
	"GFaaV752VCbgGESd4iD2j7Em6kVvrys96KHau745fT1Q",
	"uHGPX5bAWvUVTcYWQe6qbsZa3rk6ovgCkvfUmMEFesU",
])
const Wallet = ({ address }: { address: string }) => (
	<Tooltip title={address}>
		<span style={{ fontWeight: TEAM.has(address) ? 600 : undefined }}>{TEAM.has(address) ? "BAPC team" : `${address.slice(0, 4)}…${address.slice(-4)}`}</span>
	</Tooltip>
)

// Owner → owner history of one minted ape: signatures of the mint account, parsed by Helius
const fetchHistory = async (mint: string) => {
	const sigs = await fetch(RPC_HOST, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ jsonrpc: "2.0", id: "sigs", method: "getSignaturesForAddress", params: [mint, { limit: LIMIT }] }),
	}).then(r => r.json())
	if (sigs.error) throw new Error(sigs.error.message)
	const signatures = (sigs.result as { signature: string, err: unknown }[]).filter(s => !s.err).map(s => s.signature)
	if (!signatures.length) return { rows: [], truncated: false }
	const key = new URL(RPC_HOST).searchParams.get("api-key")
	const res = await fetch(`https://api-mainnet.helius-rpc.com/v0/transactions?api-key=${key}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ transactions: signatures }),
	})
	if (!res.ok) throw new Error(`Helius error ${res.status}`)
	// Full page of signatures = older ones were cut off (the filtered rows can be far fewer than LIMIT)
	return { rows: ownerChanges(await res.json() as HeliusTx[], mint), truncated: sigs.result.length === LIMIT }
}

const TransactionHistory = ({ mint }: { mint: string }) => {
	const [state, setState] = useState<Partial<Awaited<ReturnType<typeof fetchHistory>>> & { error?: boolean }>({})
	useEffect(() => {
		fetchHistory(mint).then(setState).catch((e: Error) => {
			Sentry.captureException(e)
			setState({ error: true })
		})
	}, [mint])
	const { rows, truncated, error } = state
	return (
		<Box sx={{ mt: 3, p: 2, border: "1px solid rgba(43,45,66,.15)", borderRadius: 2 }}>
			<Typography variant="subtitle2" sx={{ mb: .5 }}>Transaction history</Typography>
			{error && <Typography variant="body2" color="text.secondary">Couldn't load the history right now.</Typography>}
			{!rows && !error && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
			{rows?.length === 0 && <Typography variant="body2" color="text.secondary">No transfers found.</Typography>}
			{rows?.map(r => (
				<Typography key={r.signature + r.to} variant="body2" sx={{ py: .5 }}>
					<Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{!r.from ? "Minted" : r.lamports ? "Sold" : "Transferred"}</Box>
					{r.from && <><Wallet address={r.from} /> → </>}<Wallet address={r.to} />
					{r.lamports ? ` · ${+(r.lamports / 1e9).toFixed(3)} SOL` : ""}
					<Link href={`https://solscan.io/tx/${r.signature}`} target="_blank" rel="noopener" color="text.secondary" sx={{ ml: 1, fontSize: 12 }}>
						{new Date(r.time * 1000).toLocaleDateString()}
					</Link>
				</Typography>
			))}
			{truncated && <Typography variant="caption" color="text.secondary">Latest {LIMIT} transactions</Typography>}
		</Box>
	)
}

export default TransactionHistory
